/**
 * ProseMirror Setup: Custom menu and plugin configuration
 *
 * This module provides functions for building the ProseMirror editor's toolbar,
 * keyboard shortcuts, input rules, and plugins.
 *
 * For general ProseMirror concepts, see prosemirror/README.md
 */

import { baseKeymap, toggleMark } from "prosemirror-commands";
import { dropCursor } from "prosemirror-dropcursor";
import { gapCursor } from "prosemirror-gapcursor";
import { history, redo, undo } from "prosemirror-history";
import { ellipsis, emDash, inputRules, smartQuotes } from "prosemirror-inputrules";
import { keymap } from "prosemirror-keymap";
import { type MenuElement, menuBar as toolbar } from "prosemirror-menu";
import type { Command } from "prosemirror-state";
import { UAParser } from "ua-parser-js";

import { schema } from "../adapter/schema";
import { backspaceCommand, enterCommand, spaceCommand } from "../commands";
import { alignmentItems } from "../toolbar/alignment";
import { toggleBlockTypeItem } from "../toolbar/block-toggle";
import { DynamicColorPicker } from "../toolbar/color-picker";
import { formulaItem } from "../toolbar/formula";
import { imageItem } from "../toolbar/image";
import { indentDecreaseItem, indentIncreaseItem } from "../toolbar/indent";
import { linkItem } from "../toolbar/link";
import { markItem } from "../toolbar/mark-toggle";
import { DynamicTextStyleDropdown } from "../toolbar/text-style-dropdown";
import { isFormulaSelected, isImageSelected, isSelectionInCodeBlock } from "../utils";

import { codeBlockMergePlugin } from "./code-block-merge";

import { t } from "i18n/i18n";
import {
  toolbarAlignCenterIcon,
  toolbarAlignLeftIcon,
  toolbarAlignRightIcon,
  toolbarBoldIcon,
  toolbarBulletListIcon,
  toolbarCodeBlockIcon,
  toolbarItalicIcon,
  toolbarNumberedListIcon,
  toolbarQuoteIcon,
  toolbarUnderlineIcon,
} from "mds/utils/toolbar-icons";

const os = new UAParser(navigator.userAgent).getOS().name ?? "";
const modKey = os === "macOS" ? "⌘" : "Ctrl";

/**
 * Build input rules that transform text as you type
 *
 * Input rules are like markdown shortcuts - they automatically convert text patterns
 * into formatted content. For example, typing "--" becomes an em dash.
 *
 * @param schema - The ProseMirror schema
 * @returns A plugin containing all input rules
 */
function buildInputRules() {
  // Basic text transformations: smart quotes, ellipsis, em dashes
  const rules = smartQuotes.concat(ellipsis, emDash);

  // Return a plugin that applies these rules as the user types
  return inputRules({ rules });
}

/**
 * Build keyboard shortcuts (keymap) for the editor
 *
 * Keymaps map keyboard combinations to commands. "Mod" is a platform-agnostic
 * modifier key (Cmd on Mac, Ctrl on Windows/Linux).
 *
 * @returns A map of key combinations to commands
 */
function buildKeymap(): Record<string, Command> {
  const keys: Record<string, Command> = {};

  // History shortcuts: undo and redo
  keys["Mod-z"] = undo; // Cmd/Ctrl+Z
  keys["Shift-Mod-z"] = redo; // Cmd/Ctrl+Shift+Z

  // Mark shortcuts: toggle formatting with keyboard
  if (schema.marks.bold) {
    const toggleBold = (state, dispatch) =>
      isSelectionInCodeBlock(state) || isFormulaSelected(state) || isImageSelected(state)
        ? false
        : toggleMark(schema.marks.bold)(state, dispatch);
    keys["Mod-b"] = toggleBold;
    keys["Mod-B"] = toggleBold;
  }
  if (schema.marks.italic) {
    const toggleItalic = (state, dispatch) =>
      isSelectionInCodeBlock(state) || isFormulaSelected(state) || isImageSelected(state)
        ? false
        : toggleMark(schema.marks.italic)(state, dispatch);
    keys["Mod-i"] = toggleItalic;
    keys["Mod-I"] = toggleItalic;
  }
  if (schema.marks.underline) {
    const toggleUnderline = (state, dispatch) =>
      isSelectionInCodeBlock(state) || isFormulaSelected(state) || isImageSelected(state)
        ? false
        : toggleMark(schema.marks.underline)(state, dispatch);
    keys["Mod-u"] = toggleUnderline;
    keys["Mod-U"] = toggleUnderline;
  }

  // custom handling for list items
  keys.Enter = enterCommand;
  keys.Space = spaceCommand;
  for (const bsKey of ["Backspace", "Mod-Backspace", "Alt-Backspace", "Shift-Backspace"]) {
    keys[bsKey] = backspaceCommand;
  }

  return keys;
}

/**
 * Menu item creation helpers
 *
 * These functions create menu items for the toolbar, each performing a schema check
 * and returning the appropriate menu element or null if the schema doesn't support it.
 */
function createTextStyleDropdownItem(): MenuElement | null {
  if (!schema.nodes.heading || !schema.nodes.paragraph) return null;
  return new DynamicTextStyleDropdown();
}

function createBoldItem(): MenuElement | null {
  if (!schema.marks.bold) return null;

  return markItem(schema.marks.bold, {
    title: `${t("worksheet.rich_text_editor.toggle_bold")} (${modKey}+B)`,
    icon: toolbarBoldIcon,
  });
}

function createItalicItem(): MenuElement | null {
  if (!schema.marks.italic) return null;

  return markItem(schema.marks.italic, {
    title: `${t("worksheet.rich_text_editor.toggle_italic")} (${modKey}+I)`,
    icon: toolbarItalicIcon,
  });
}

function createUnderlineItem(): MenuElement | null {
  if (!schema.marks.underline) return null;

  return markItem(schema.marks.underline, {
    title: `${t("worksheet.rich_text_editor.toggle_underline")} (${modKey}+U)`,
    icon: toolbarUnderlineIcon,
  });
}

function createLinkItem(): MenuElement | null {
  if (!schema.marks.link) return null;
  return linkItem(schema.marks.link);
}

function createImageItem(): MenuElement | null {
  if (!schema.nodes.image) return null;
  return imageItem();
}

function createFormulaItem(): MenuElement | null {
  if (!schema.nodes.formula) return null;
  return formulaItem();
}

function createQuoteItem(): MenuElement | null {
  if (!schema.nodes.quote) return null;

  return toggleBlockTypeItem(schema.nodes.quote, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.toggle_quote"),
    icon: toolbarQuoteIcon,
  });
}

function createCodeBlockItem(): MenuElement | null {
  if (!schema.nodes.code_block) return null;

  return toggleBlockTypeItem(schema.nodes.code_block, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.toggle_code_block"),
    icon: toolbarCodeBlockIcon,
  });
}

function createOrderedListItem(): MenuElement | null {
  if (!schema.nodes.ordered_list_item) return null;

  return toggleBlockTypeItem(schema.nodes.ordered_list_item, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.ordered_list_item"),
    icon: toolbarNumberedListIcon,
  });
}

function createBulletListItem(): MenuElement | null {
  if (!schema.nodes.bullet_list_item) return null;

  return toggleBlockTypeItem(schema.nodes.bullet_list_item, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.bullet_list_item"),
    icon: toolbarBulletListIcon,
  });
}

function createIndentIncreaseItem(): MenuElement | null {
  return indentIncreaseItem;
}

function createIndentDecreaseItem(): MenuElement | null {
  return indentDecreaseItem;
}

function createColorPickerItem(): MenuElement | null {
  if (!schema.marks.color) return null;
  return new DynamicColorPicker();
}

function createAlignmentItems(): MenuElement[] {
  return alignmentItems([
    {
      value: "left",
      icon: toolbarAlignLeftIcon,
      title: t("worksheet.rich_text_editor.align_left"),
    },
    {
      value: "center",
      icon: toolbarAlignCenterIcon,
      title: t("worksheet.rich_text_editor.align_center"),
    },
    {
      value: "right",
      icon: toolbarAlignRightIcon,
      title: t("worksheet.rich_text_editor.align_right"),
    },
  ]);
}

/**
 * Build a menu group from multiple creator functions, filtering out null values.
 * Creators may return a single item, an array of items, or null.
 */
function buildGroup(...creators: Array<() => MenuElement | MenuElement[] | null>): MenuElement[] {
  return creators.flatMap((creator) => creator() ?? []);
}

/**
 * Build the menu structure for the toolbar
 *
 * The menu is organized as an array of menu groups (rows). Each group is an array
 * of menu elements (buttons, dropdowns, etc.) that appear together in the toolbar.
 *
 * @returns An array of menu groups, where each group is a row in the toolbar
 */
function buildToolbar(): MenuElement[][] {
  return [
    // Group 1: Text style, Bold, Italic, Underline
    buildGroup(createTextStyleDropdownItem, createBoldItem, createItalicItem, createUnderlineItem),
    // Group 2: Bullet list, Numbered list, Indent, Outdent, Text color
    buildGroup(
      createBulletListItem,
      createOrderedListItem,
      createIndentIncreaseItem,
      createIndentDecreaseItem,
      createColorPickerItem,
    ),
    // Group 3: Link, Image, Quote, Code block, Formula
    buildGroup(
      createLinkItem,
      createImageItem,
      createQuoteItem,
      createCodeBlockItem,
      createFormulaItem,
    ),
    // Group 4: Align left, Align center, Align right
    buildGroup(createAlignmentItems),
  ];
}

/**
 * Create the base set of plugins for the editor
 *
 * Plugins extend the editor's functionality. This function assembles all the plugins
 * needed for a fully functional rich text editor.
 *
 * @param showToolbar - Whether to include the toolbar
 * @returns An array of plugins to be passed to EditorState.create()
 */
export function createBasePlugins(showToolbar: boolean) {
  const plugins = [
    // Input rules: Transform text as you type (smart quotes, ellipsis, em dashes, etc.)
    buildInputRules(),
    // Custom keyboard shortcuts (Cmd+B for bold, Cmd+I for italic, etc.)
    keymap(buildKeymap()),
    // Base keymap: Standard editing shortcuts (Enter, Backspace, Arrow keys, etc.)
    keymap(baseKeymap),
    // Drop cursor: Shows a visual indicator when dragging content to drop it
    dropCursor(),
    // Gap cursor: Allows clicking in empty areas between block nodes
    gapCursor(),
    // History: Enables undo/redo functionality
    history(),
    // Code block: Merges adjacent code blocks after a transaction
    codeBlockMergePlugin(),
  ];

  // Conditionally add the menu bar (toolbar) if not in read-only mode
  if (showToolbar) {
    plugins.push(
      toolbar({
        floating: false, // Fixed at the bottom
        position: "after", // Below the editor
        content: buildToolbar(),
      }),
    );
  }

  return plugins;
}
